import {
    ColDef,
    SuppressKeyboardEventParams,
    CellClassFunc,
    ColumnApi,
    FillOperationParams,
    SortModelItem,
} from "ag-grid-community";
import { IDate } from "components/product-collection/product-collection-views/product-collection-grid-view/grid-editors/editor-helper";

/**
 * `mergedCellClass` is a factory that combines and resolve multiple cellClasses.
 *
 * AgGrid colDef's `cellClass` property can be either: string, string[] or function.
 *
 * `mergedCellClass` function merge all 3 of the above types and return an array of strings by:
 * 1. Function -> call with params to get the string or array of strings
 * 2. String -> return as an element of an array
 * 3. String[] -> return as multiple elements of an array by using flatMap
 *
 * For example,
 * mergedCellClass('c1', ['c2'], () => 'c3', () => ['c4']) returns the function
 * (params) => ['c1','c2','c3','c4']
 *
 * The returned value of this function can be supplied to colDef as `cellClass`
 *
 * @param cellClasses - An array of cellClasses, which is of type string | string[] | function
 */
export const mergedCellClass =
    (...cellClasses: ColDef["cellClass"][]): CellClassFunc =>
    (params) =>
        cellClasses.flatMap((cellClass) => {
            return typeof cellClass === "function" ? cellClass(params) : (cellClass ?? []);
        });

const resetCells = (columns, rowNode, resetValue?) => {
    columns.forEach((column) => {
        if (column.isCellEditable(rowNode)) {
            rowNode.setDataValue(column.getId(), resetValue);
        }
    });
};

export const resetSelectedCellRanges = (gridApi, resetValue?) => {
    const [cellRange] = gridApi.getCellRanges();

    if (cellRange === undefined) {
        return;
    }
    const { rowIndex: startRowIndex, rowPinned: startRowPinned } = cellRange.startRow;
    const { rowIndex: endRowIndex, rowPinned: endRowPinned } = cellRange.endRow;
    const firstIndex = Math.min(startRowIndex, endRowIndex);
    const lastIndex = Math.max(startRowIndex, endRowIndex);

    // We won't allow to delete bulk cells on pinned rows
    if (startRowPinned === "top" || endRowPinned === "top") {
        return;
    }

    for (let rowIndex = firstIndex; rowIndex <= lastIndex; rowIndex++) {
        const rowNode = gridApi.getDisplayedRowAtIndex(rowIndex);
        resetCells(cellRange.columns, rowNode, resetValue);
    }

    const focusedCell = gridApi.getFocusedCell();
    gridApi.stopEditing();
    gridApi.setFocusedCell(focusedCell.rowIndex, focusedCell.column.getColId(), null);
};

export const escapeHTML = (html) => {
    const div = document.createElement("div");
    div.innerText = html;
    return div.innerHTML;
};

export const getSortModelFromApi = (columnApi: ColumnApi): SortModelItem[] => {
    const columnState = columnApi.getColumnState();
    const filteredStates = columnState.filter((item) => item.sort != null);

    const indexes: { [colId: string]: number } = {};
    filteredStates.forEach((state) => {
        const id = state.colId as string;
        indexes[id] = state.sortIndex;
    });

    const res = filteredStates.map((s) => {
        return { colId: s.colId, sort: s.sort };
    });

    res.sort((a: any, b: any) => indexes[a.colId] - indexes[b.colId]);

    return res;
};

export const suppressDeleteBackspaceKeyboard = (params: SuppressKeyboardEventParams) => {
    // Backspace and Delete are for clearing value. We don't want it to open cell editor
    const KEY_BACKSPACE = "Backspace";
    const KEY_DELETE = "Delete";
    const event = params.event;
    const key = event.key;
    let keysToSuppress = [];

    if (!params.editing) {
        keysToSuppress = keysToSuppress.concat(KEY_BACKSPACE, KEY_DELETE);
    }
    const suppress = keysToSuppress.some(function (suppressedKey) {
        return suppressedKey === key || key.toUpperCase() === suppressedKey;
    });
    return suppress;
};

// Function to check if values are valid dates of the mm/dd/yyyy format
// this being the reason it's not a helper function
export const isDateString = (value: any): value is string => {
    const dateRegex = /[0-9]{2}\/[0-9]{2}\/[0-9]{4}/;
    return dateRegex.test(value) && !Number.isNaN(new Date(value));
};

export const handleDateValuesOnFillHandle = (params: {
    initialValues: FillOperationParams["initialValues"];
    values: FillOperationParams["values"];
}) => {
    const { initialValues, values } = params;

    const patternInDays = getPatternFromDates(initialValues);

    if (!patternInDays) {
        return false;
    }

    const lastDateInValues = values[values.length - 1];
    const lastDate = new Date(lastDateInValues);

    // Applying the pattern to the item that will be returned and used by AG Grid
    lastDate.setDate(lastDate.getDate() + patternInDays);

    const formatDateOptions = {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
    } as const;

    // Formatting back the date to the same format as the inputs
    const formattedDate = new Date(lastDate).toLocaleDateString("en-US", formatDateOptions);
    return formattedDate;
};

const getPatternFromDates = (dates: string[] | Date[]) => {
    // If there is only one date don't want to create a pattern
    if (dates.length === 1) {
        return 0;
    }

    let patternInDays: number | undefined = undefined;
    if (dates.length > 0) {
        for (let i = 0; i < dates.length; i++) {
            // if there's only one item, the pattern remains +1 day
            if (i === 0) {
                continue;
            }

            const currentDate = new Date(dates[i]);
            const previousDate = new Date(dates[i - 1]);

            // Getting the difference in days from the previous item and current item
            const diffInDays = Math.floor(
                (Date.UTC(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate()) -
                    Date.UTC(previousDate.getFullYear(), previousDate.getMonth(), previousDate.getDate())) /
                    (1000 * 60 * 60 * 24),
            );

            // Checking to see if there is a pattern, if there is and it's the,
            // same as the previous one, it will maintain it as the current pattern
            // otherwise will erase the pattern since there's none and break the loop
            if (patternInDays) {
                const patternsAreEqual = patternInDays === diffInDays;
                if (!patternsAreEqual) {
                    patternInDays = undefined;
                    break;
                }
            }

            patternInDays = diffInDays;
        }
    }

    return patternInDays;
};

export const mapValuesToDateObj = (value: Date | string): IDate | undefined => {
    if (value instanceof Date) {
        return {
            year: value.getFullYear(),
            month: value.getMonth() + 1,
            day: value.getDate(),
        };
    }

    const dateValue = new Date(value);

    if (Number.isNaN(dateValue?.getTime())) {
        return;
    }

    return {
        year: dateValue.getFullYear(),
        month: dateValue.getMonth() + 1,
        day: dateValue.getDate(),
    };
};

export const handleFillOperation = (params: FillOperationParams) => {
    const { initialValues, values } = params;

    const valuesAreDates = initialValues.every((v) => isDateString(v));

    if (valuesAreDates) {
        return handleDateValuesOnFillHandle({ initialValues, values });
    }

    return false;
};
